import React, { FC, useEffect, useState } from 'react';
import { Button, Card, OverlayTrigger, Pagination, Table, Tooltip } from 'react-bootstrap';
import Moment from 'react-moment';
import { Link, RouteComponentProps } from 'react-router-dom';
import useSubmissionList from '../../../hooks/api/submission/useSubmissionList';
import _ from 'lodash';
import { noop } from '../../../utilities/FunctionUtilities';
import { getSecuritySection, SecuritySections, SecurityModel } from '../../../models/security/security';
import { useIntl } from 'react-intl';
import { formatToString } from '../../../components/form/public/payment/fieldToString';
import { FieldType } from '../../../models/form/fieldType';
import EmptyList from '../../../components/layout/emptyList/EmptyList';
import LoadingSpinner from '../../../components/layout/loadingSpinner/LoadingSpinner';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { useHistory } from 'react-router';
import { getText } from '../../../utilities/CultureUtilities';
import { FallbackPage } from '../../../components/layout/navbar/Navbar';

const excludedFieldType: FieldType[] = [
  FieldType.Toggle,
  FieldType.CheckBox,
  FieldType.Video,
  FieldType.EmbeddedPhoto,
  FieldType.TakePicture,
  FieldType.DocumentViewer,
  FieldType.UploadFile,
  FieldType.WebLink,
  FieldType.Payment,
  FieldType.Acknowledgment,
  FieldType.Captcha,
  FieldType.Signature,
  FieldType.HTML,
  FieldType.Select,
  FieldType.PaymentOptionList,
  FieldType.PaymentChoiceList,
  FieldType.OptionList,
  FieldType.Products,
];

const nbPerPage = 8;

interface MatchParams {
  formId: string;
}

type header = { id: string; name: string };

type Props = RouteComponentProps<MatchParams>;

const SubmissionListPage: FC<Props> = ({
  match: {
    params: { formId },
  },
}: Props) => {
  const {
    currentSubmissionList,
    totalCount,
    setCurrentFormId,
    isLoading,
    currentFormSteps,
    currentFormName,
    reloadSubmissions,
  } = useSubmissionList(formId, nbPerPage);
  const [nbOfPages, setNbOfPages] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [headers, setHeaders] = useState<header[]>([]);
  const [formSubmissionSecurity, setFormSubmissionSecurity] = useState<SecurityModel | null>(null);
  const [formName, setFormName] = useState<string>('');
  const security = useSelector((state: RootState) => state.organization.organization?.security);
  const intl = useIntl();
  const history = useHistory();

  useEffect(() => {
    if (formId) {
      setCurrentFormId(formId);
    }
  }, [currentSubmissionList]);

  useEffect(() => {
    setNbOfPages(Math.ceil(totalCount / nbPerPage));
  }, [totalCount]);

  useEffect(() => {
    if (currentFormSteps && currentFormSteps.length > 0) {
      createColumnHeaders();
    }
  }, [currentFormSteps]);

  useEffect(() => {
    if (currentFormName) {
      setFormName(currentFormName);
    }
  }, [currentFormName]);

  useEffect(() => {
    if (security) {
      setFormSubmissionSecurity(getSecuritySection(security, SecuritySections.FORM_SUBMISSION));
    }
    if (formSubmissionSecurity && !formSubmissionSecurity?.read) {
      history.push(FallbackPage);
    }
  }, [security, formSubmissionSecurity]);

  const handleItemClick = (event) => {
    if (event.target.text != currentPage) {
      const newPage = Number(event.target.text);
      setCurrentPage(newPage);
      reloadSubmissions(newPage);
    }
  };

  const handleFirstClick = () => {
    setCurrentPage(1);
    reloadSubmissions(1);
  };

  const handleLastClick = () => {
    setCurrentPage(nbOfPages);
    reloadSubmissions(nbOfPages);
  };

  const handleNextClick = () => {
    if (currentPage >= nbOfPages) {
      return;
    } else {
      const newPage = currentPage + 1;
      setCurrentPage(newPage);
      reloadSubmissions(newPage);
    }
  };

  const handlePrevClick = () => {
    if (currentPage <= 1) {
      return;
    } else {
      const newPage = currentPage - 1;
      setCurrentPage(newPage);
      reloadSubmissions(newPage);
    }
  };

  const createColumnHeaders = () => {
    const headersList: header[] = [];
    const formStep = currentFormSteps[0];
    for (const section of formStep.sections) {
      for (const field of section.fields) {
        if (!excludedFieldType.includes(field.type)) {
          headersList.push({ id: field.id, name: getText(field.text, intl.locale) });
          if (headersList.length == 4) {
            setHeaders(headersList);
            headersList.push({ id: 'date', name: 'Date' });
            return;
          }
        }
      }
    }
    headersList.push({ id: 'date', name: 'Date' });
    setHeaders(headersList);
  };

  const renderTooltip = (text) => <Tooltip id="button-tooltip">{text}</Tooltip>;

  return (
    <Card>
      <Card.Header className="d-inline-flex align-items-center justify-content-start">
        <span>{intl.formatMessage({ id: 'formList.submission.formName' })}</span>
        <span className="form-name">{getText(formName, intl.locale)}</span>
      </Card.Header>
      {isLoading && <LoadingSpinner />}
      <Card.Body>
        {!isLoading && currentSubmissionList && currentSubmissionList.length > 0 && (
          <Table hover responsive>
            <thead>
              <tr>
                {headers &&
                  headers.map((header) => {
                    return (
                      <th className="submissions-list-header" key={header.id}>
                        {header.name}
                      </th>
                    );
                  })}
                <th key="actions" className="text-end submissions-list-header">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {currentSubmissionList.map((row) => {
                return (
                  <tr key={row.id}>
                    {headers.map((key) => {
                      if (key.id === 'date') {
                        return (
                          <td key={`${row.id}-${key.id}`}>
                            <Moment format="LLL">{row[key.id]}</Moment>
                          </td>
                        );
                      }
                      let fieldValue = '';
                      const field = _.find(row.fields, { id: key.id });
                      if (field) {
                        fieldValue = formatToString(field, JSON.parse(field.value).value) || '';
                      }
                      return <td key={`${row.id}-${key.id}`}>{fieldValue}</td>;
                    })}
                    <td className="text-end">
                      <OverlayTrigger
                        delay={{ show: 50, hide: 100 }}
                        overlay={renderTooltip(intl.formatMessage({ id: 'formList.submission.view' }))}
                        placement="top"
                      >
                        <Button
                          variant="primary"
                          className="d-inline-flex align-items-center justify-content-center me-1"
                          onClick={() => history.push(`/registrations/submissions/${formId}/${row.id}`)}
                          disabled={!formSubmissionSecurity?.read}
                        >
                          <div className="d-inline-flex">
                            <span className="mdi mdi mdi-eye-settings-outline"></span>
                          </div>
                        </Button>
                      </OverlayTrigger>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {!isLoading && currentSubmissionList && currentSubmissionList.length > 0 && nbOfPages && nbOfPages > 1 && (
          <Pagination>
            <Pagination.First onClick={handleFirstClick} />
            <Pagination.Prev onClick={handlePrevClick} />
            {_.range(0, nbOfPages, 1).map((idx) => {
              return (
                <Pagination.Item
                  key={idx}
                  active={idx + 1 === currentPage}
                  onClick={idx + 1 === currentPage ? noop : handleItemClick}
                >
                  {' '}
                  {idx + 1}
                </Pagination.Item>
              );
            })}
            <Pagination.Next onClick={handleNextClick} />
            <Pagination.Last onClick={handleLastClick} />
          </Pagination>
        )}
        {!isLoading && currentSubmissionList && currentSubmissionList.length == 0 && (
          <div className="d-flex flex-column justify-content-center align-items-center">
            <EmptyList message={intl.formatMessage({ id: 'formList.submission.noSubmissions' })} />
            <Link to={`/registrations`}>
              <Button variant="primary" className="me-1">
                <span>{intl.formatMessage({ id: 'formList.buttons.returnToForms' })}</span>
              </Button>
            </Link>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default SubmissionListPage;
